/**
 * Messenger Icon Component
 *
 * Header icon that displays unread message badge and opens the messenger widget.
 * Shows a chat bubble icon with optional unread count indicator.
 */

import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { messengerWsService } from '../services/messenger-websocket';
import { messengerAPI } from '../services/messenger-api';
import { eventBus, Events } from '../services/events';

// Import messenger widget
import './messenger-widget';

@customElement('moya-messenger-icon')
export class MoyaMessengerIcon extends LitElement {
  static styles = css`
    :host {
      display: inline-block;
      position: relative;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    }

    .trigger {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 36px;
      height: 36px;
      border: none;
      background: transparent;
      border-radius: 8px;
      cursor: pointer;
      color: rgba(255, 255, 255, 0.7);
      transition: all 0.15s ease;
      position: relative;
    }

    .trigger:hover {
      background: rgba(255, 255, 255, 0.1);
      color: white;
    }

    .trigger:focus {
      outline: none;
      box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.5);
    }

    .trigger.active {
      background: rgba(255, 255, 255, 0.15);
      color: white;
    }

    .trigger svg {
      width: 20px;
      height: 20px;
    }

    .badge {
      position: absolute;
      top: 2px;
      right: 2px;
      min-width: 16px;
      height: 16px;
      padding: 0 4px;
      background: #ef4444;
      border-radius: 8px;
      font-size: 10px;
      font-weight: 600;
      color: white;
      display: flex;
      align-items: center;
      justify-content: center;
      line-height: 1;
    }

    .badge.large {
      font-size: 9px;
    }

    /* Pulse animation for new messages */
    .badge.pulse {
      animation: pulse 1s ease-in-out;
    }

    @keyframes pulse {
      0% {
        transform: scale(1);
      }
      50% {
        transform: scale(1.2);
      }
      100% {
        transform: scale(1);
      }
    }

    /* Connection indicator */
    .connection-indicator {
      position: absolute;
      bottom: 2px;
      right: 2px;
      width: 8px;
      height: 8px;
      border-radius: 50%;
      border: 1.5px solid #1e293b;
    }

    .connection-indicator.connected {
      background: #22c55e;
    }

    .connection-indicator.disconnected {
      background: #ef4444;
    }

    .connection-indicator.connecting {
      background: #f59e0b;
      animation: blink 1s infinite;
    }

    @keyframes blink {
      0%, 100% {
        opacity: 1;
      }
      50% {
        opacity: 0.5;
      }
    }
  `;

  @property({ type: String }) token = '';
  @property({ type: String }) messengerUrl = '';
  @property({ type: String }) messengerApiUrl = '';
  @property({ type: String }) userId = '';

  @state() private unreadCount = 0;
  @state() private isWidgetOpen = false;
  @state() private connectionStatus: 'connecting' | 'connected' | 'disconnected' = 'disconnected';
  @state() private shouldPulse = false;

  private unsubscribers: (() => void)[] = [];
  private messengerInitialized = false;

  connectedCallback() {
    super.connectedCallback();
    this.setupEventListeners();
    this.initializeMessenger();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.unsubscribers.forEach((unsub) => unsub());
    this.unsubscribers = [];
  }

  private setupEventListeners() {
    // MESSENGER_UNREAD_CHANGED is the single source of truth for the badge
    // count. A single inbound message emits BOTH this event (increment:1) and
    // MESSENGER_MESSAGE_RECEIVED, so the count must only be touched here —
    // otherwise every message was counted twice.
    this.unsubscribers.push(
      eventBus.on(Events.MESSENGER_UNREAD_CHANGED, (data: unknown) => {
        const { increment, unreadCount } = data as { increment?: number; unreadCount?: number };
        if (typeof unreadCount === 'number') {
          // Authoritative absolute count (e.g. conversation.updated / reads).
          this.unreadCount = Math.max(0, unreadCount);
        } else if (typeof increment === 'number') {
          // Relative bump from an inbound message. Ignore while the widget is
          // open — the user is actively reading, so nothing is unread.
          if (this.isWidgetOpen) return;
          this.unreadCount = Math.max(0, this.unreadCount + increment);
        }
      })
    );

    // New-message event drives only the visual pulse cue; the count itself is
    // owned by MESSENGER_UNREAD_CHANGED above (no increment here).
    this.unsubscribers.push(
      eventBus.on(Events.MESSENGER_MESSAGE_RECEIVED, () => {
        if (!this.isWidgetOpen) {
          this.triggerPulse();
        }
      })
    );

    // Listen for connection status
    this.unsubscribers.push(
      eventBus.on(Events.WS_CONNECTED, (data: unknown) => {
        if ((data as { service?: string })?.service === 'messenger') {
          this.connectionStatus = 'connected';
        }
      })
    );

    this.unsubscribers.push(
      eventBus.on(Events.WS_DISCONNECTED, (data: unknown) => {
        if ((data as { service?: string })?.service === 'messenger') {
          this.connectionStatus = 'disconnected';
        }
      })
    );

    // Listen for widget close
    this.unsubscribers.push(
      eventBus.on(Events.MESSENGER_WIDGET_CLOSED, () => {
        this.isWidgetOpen = false;
      })
    );
  }

  private async initializeMessenger() {
    if (!this.token || !this.messengerUrl || this.messengerInitialized) return;
    this.messengerInitialized = true;

    // Configure services
    const apiUrl = this.messengerApiUrl || this.messengerUrl.replace('wss://', 'https://').replace('ws://', 'http://');
    messengerAPI.configure(apiUrl, this.token);
    messengerWsService.configure(this.messengerUrl, this.token);

    // Connect WebSocket
    this.connectionStatus = 'connecting';
    messengerWsService.connect();

    // Fetch initial unread count
    try {
      const { total } = await messengerAPI.getUnreadCount();
      this.unreadCount = total;
    } catch (e) {
      console.error('[MessengerIcon] Failed to get unread count:', e);
    }
  }

  updated(changedProperties: Map<string, unknown>) {
    // Handle deferred initialization: in Lit, property bindings from parent
    // templates are applied AFTER connectedCallback(), so initializeMessenger()
    // may have returned early. Re-attempt when properties arrive.
    if (
      (changedProperties.has('token') || changedProperties.has('messengerUrl')) &&
      this.token &&
      this.messengerUrl &&
      !this.messengerInitialized
    ) {
      this.initializeMessenger();
      return;
    }

    // Update token on already-initialized services
    if (changedProperties.has('token') && this.token && this.messengerInitialized) {
      messengerAPI.setToken(this.token);
      messengerWsService.setToken(this.token);
    }
  }

  private toggleWidget() {
    this.isWidgetOpen = !this.isWidgetOpen;

    if (this.isWidgetOpen) {
      this.dispatchEvent(
        new CustomEvent('moya-messenger-open', {
          bubbles: true,
          composed: true,
          detail: { mode: 'mini' },
        })
      );
      eventBus.emit(Events.MESSENGER_WIDGET_OPENED, { mode: 'mini' });
    } else {
      this.dispatchEvent(
        new CustomEvent('moya-messenger-close', {
          bubbles: true,
          composed: true,
        })
      );
      eventBus.emit(Events.MESSENGER_WIDGET_CLOSED, {});
    }
  }

  private handleWidgetClose() {
    this.isWidgetOpen = false;
    this.dispatchEvent(
      new CustomEvent('moya-messenger-close', {
        bubbles: true,
        composed: true,
      })
    );
  }

  private pulseTimer: ReturnType<typeof setTimeout> | null = null;

  /** Briefly play the badge pulse animation. */
  private triggerPulse() {
    this.shouldPulse = true;
    if (this.pulseTimer) clearTimeout(this.pulseTimer);
    this.pulseTimer = setTimeout(() => {
      this.shouldPulse = false;
      this.pulseTimer = null;
    }, 1000);
  }

  private formatBadgeCount(count: number): string {
    if (count > 99) return '99+';
    return String(count);
  }

  render() {
    const triggerClasses = classMap({
      trigger: true,
      active: this.isWidgetOpen,
    });

    const badgeClasses = classMap({
      badge: true,
      large: this.unreadCount > 99,
      pulse: this.shouldPulse,
    });

    const connectionClasses = classMap({
      'connection-indicator': true,
      [this.connectionStatus]: true,
    });

    return html`
      <button
        class="${triggerClasses}"
        @click="${this.toggleWidget}"
        aria-label="Open messenger${this.unreadCount > 0 ? ` (${this.unreadCount} unread)` : ''}"
        aria-expanded="${this.isWidgetOpen}"
        aria-haspopup="dialog"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
        ${this.unreadCount > 0
          ? html`<span class="${badgeClasses}">${this.formatBadgeCount(this.unreadCount)}</span>`
          : ''}
        <span class="${connectionClasses}"></span>
      </button>

      ${this.isWidgetOpen
        ? html`
            <moya-messenger-widget
              .token="${this.token}"
              .messengerUrl="${this.messengerUrl}"
              .userId="${this.userId}"
              mode="${window.innerWidth <= 640 ? 'full' : 'mini'}"
              @moya-messenger-close="${this.handleWidgetClose}"
            ></moya-messenger-widget>
          `
        : ''}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'moya-messenger-icon': MoyaMessengerIcon;
  }
}
